export interface SessionStatus {
  id: string;
  serverNow: string;
  idleExpiresAt: string;
  absoluteExpiresAt: string;
}

type SessionView = { warning: boolean; seconds: number; expired: boolean; absolute: boolean };
type Transport = {
  get<T>(path: string): Promise<T>;
  post<T>(path: string): Promise<T>;
  delete(path: string): Promise<void>;
};

const endpoint = '/api/v1/auth/session';
const initial: SessionView = { warning: false, seconds: 0, expired: false, absolute: false };

export class ApplicationSession {
  private status: SessionStatus | null = null;
  private offset = 0;
  private view = initial;
  private listeners = new Set<() => void>();
  private logout: (() => void) | null = null;
  private lastActivitySent = 0;
  private activityTimer: ReturnType<typeof setTimeout> | undefined;
  private timer: ReturnType<typeof setInterval> | undefined;
  private lastCheck = 0;
  private checking = false;
  private updating = false;
  private generation = 0;
  private readonly storageKey: string;

  constructor(private transport: Transport, app: string) {
    this.storageKey = `classifai-session-${app}`;
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  getSnapshot = () => this.view;
  isActive = () => this.status !== null && !this.view.expired;
  setLogout = (logout: (() => void) | null) => { this.logout = logout; };

  private publish(view: SessionView) {
    if (JSON.stringify(view) === JSON.stringify(this.view)) return;
    this.view = view;
    this.listeners.forEach((listener) => listener());
  }

  private broadcast(expired = false) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ status: this.status, expired, sentAt: Date.now() }));
    } catch { /* Backend checks still enforce expiry if browser storage is unavailable. */ }
  }

  private receive = (event: StorageEvent) => {
    if (event.key !== this.storageKey || !event.newValue || !this.status) return;
    try {
      const message = JSON.parse(event.newValue) as { status?: SessionStatus; expired?: boolean };
      if (message.status?.id !== this.status.id) return;
      if (message.expired) this.expire(false);
      // Storage messages are hints, never authoritative session extensions.
      else void this.check();
    } catch { /* Ignore unrelated/malformed browser messages. */ }
  };

  private onExpired = () => this.expire();
  private onActivity = (event: Event) => {
    if (event.isTrusted && document.visibilityState === 'visible') this.activity();
  };
  private onResume = () => {
    if (document.visibilityState === 'visible') {
      this.tick();
      void this.check();
    }
  };

  async start() {
    const generation = this.generation;
    const status = await this.transport.post<SessionStatus>(endpoint);
    if (generation !== this.generation) throw new Error('Session ended while signing in.');
    this.accept(status);
    if (!this.timer) {
      window.addEventListener('storage', this.receive);
      window.addEventListener('app-session-expired', this.onExpired);
      window.addEventListener('focus', this.onResume);
      document.addEventListener('visibilitychange', this.onResume);
      for (const name of ['pointerdown', 'keydown', 'scroll', 'touchstart']) {
        window.addEventListener(name, this.onActivity, { passive: true, capture: true });
      }
      this.timer = setInterval(() => {
        this.tick();
        if (Date.now() - this.lastCheck >= 30_000) void this.check();
      }, 1000);
    }
    this.broadcast();
  }

  private accept(status: SessionStatus) {
    if (this.status?.id === status.id && Date.parse(status.serverNow) < Date.parse(this.status.serverNow)) return;
    this.status = status;
    this.offset = Date.parse(status.serverNow) - Date.now();
    this.lastCheck = Date.now();
    this.tick();
  }

  tick = () => {
    if (!this.status) return;
    const idle = Date.parse(this.status.idleExpiresAt);
    const absolute = Date.parse(this.status.absoluteExpiresAt);
    const remaining = Math.min(idle, absolute) - (Date.now() + this.offset);
    if (remaining <= 0) {
      // Another tab may have extended the server session since our last poll.
      // Keep the warning modal up while confirming, rather than logging out an active tab.
      this.publish({ warning: true, seconds: 0, expired: false, absolute: absolute <= idle });
      void this.check();
      return;
    }
    this.publish({ warning: remaining <= 60_000, seconds: remaining <= 60_000 ? Math.ceil(remaining / 1000) : 0,
      expired: false, absolute: absolute <= idle });
  };

  private async check() {
    if (!this.status || this.checking || this.view.expired) return;
    this.checking = true;
    this.lastCheck = Date.now();
    const generation = this.generation;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const status = await Promise.race([
        this.transport.get<SessionStatus>(endpoint),
        new Promise<never>((_, reject) => { timeout = setTimeout(() => reject(new Error('Session check timed out')), 5000); }),
      ]);
      if (generation !== this.generation) return;
      if (Math.min(Date.parse(status.idleExpiresAt), Date.parse(status.absoluteExpiresAt)) <= Date.parse(status.serverNow)) this.expire();
      else this.accept(status);
    } catch {
      if (generation === this.generation && this.status
        && Math.min(Date.parse(this.status.idleExpiresAt), Date.parse(this.status.absoluteExpiresAt)) <= Date.now() + this.offset) this.expire();
    }
    finally { clearTimeout(timeout); this.checking = false; }
  }

  activity = () => {
    this.tick();
    if (!this.status || this.view.warning || this.view.expired || this.activityTimer) return;
    const delay = Math.max(0, 30_000 - (Date.now() - this.lastActivitySent));
    this.activityTimer = setTimeout(() => {
      this.activityTimer = undefined;
      void this.staySignedIn().catch(() => { /* Retry on the next interaction. */ });
    }, delay);
  };

  staySignedIn = async () => {
    this.tick();
    if (!this.status || this.view.expired || this.updating) return;
    this.updating = true;
    const generation = this.generation;
    try {
      const status = await this.transport.post<SessionStatus>(`${endpoint}/activity`);
      if (generation !== this.generation) return;
      this.lastActivitySent = Date.now();
      this.accept(status);
      this.broadcast();
    } finally { this.updating = false; }
  };

  // Called before the app clears its bearer token, including on manual logout.
  end = () => {
    if (this.status) {
      this.broadcast(true);
    }
    // Also revoke when bootstrap is still in flight.
    void this.transport.delete(endpoint).catch(() => { /* Local logout must still complete offline. */ });
    this.generation++;
    this.status = null;
    clearInterval(this.timer);
    clearTimeout(this.activityTimer);
    this.timer = undefined;
    this.activityTimer = undefined;
    window.removeEventListener('storage', this.receive);
    window.removeEventListener('app-session-expired', this.onExpired);
    window.removeEventListener('focus', this.onResume);
    document.removeEventListener('visibilitychange', this.onResume);
    for (const name of ['pointerdown', 'keydown', 'scroll', 'touchstart']) {
      window.removeEventListener(name, this.onActivity, true);
    }
  };

  expire = (broadcast = true) => {
    if (this.view.expired) return;
    if (broadcast) this.broadcast(true);
    this.publish({ warning: false, seconds: 0, expired: true, absolute: false });
    this.end();
    this.logout?.();
  };
}
